<div class="up-page">
  <p *ngIf="failed()" class="up-error" role="alert">{{ 'userProfile.loadFailed' | translate }}</p>
  <p *ngIf="loading()" class="up-loading">{{ 'common.loading' | translate }}</p>

  <ng-container *ngIf="profile() as p">
    <!-- ================= En-tete contextuelle ================= -->
    <section class="up-card">
      <div class="up-head">
        <span class="up-avatar" [attr.data-segment]="p.segment">
          <img *ngIf="p.avatarUrl" [src]="p.avatarUrl" alt="" />
          <ng-container *ngIf="!p.avatarUrl">{{ initials() }}</ng-container>
        </span>
        <div>
          <h1>
            {{ p.firstName }} {{ p.lastName }}
            <span class="up-heat" [attr.data-segment]="p.segment">
              {{ 'segments.key.' + p.segment | translate }}
              <small *ngIf="p.lastActionDaysSince !== null">· {{ 'userProfile.actedAgo' | translate: { days: p.lastActionDaysSince } }}</small>
            </span>
          </h1>
          <p class="up-idline">
            {{ p.email }}<ng-container *ngIf="p.phone"> · {{ p.phone }}</ng-container>
            · {{ p.city }}<ng-container *ngIf="p.sex"> · {{ 'userProfile.sex.' + p.sex | translate }}</ng-container>
          </p>
        </div>
        <div class="up-facts">
          <span class="up-fact"><b>{{ p.involvedCount }}</b><small>{{ 'userProfile.factActivities' | translate }}</small></span>
          <span class="up-fact"><b>{{ p.createdCount }}</b><small>{{ 'userProfile.factCreated' | translate }}</small></span>
          <span class="up-fact"><b>{{ p.groups.length }}</b><small>{{ 'userProfile.factGroups' | translate }}</small></span>
          <span class="up-fact"><b>{{ euros(p.money.paid) }}</b><small>{{ 'userProfile.factPaid' | translate }}</small></span>
        </div>
      </div>

      <!-- Chaque chiffre est situe dans la base, jamais seul. -->
      <div class="up-ctx">
        <span class="up-c" *ngIf="membershipMonths() !== null">
          <b>{{ 'userProfile.months' | translate: { months: membershipMonths() } }}</b>
          <small>{{ 'userProfile.membershipAge' | translate }}<br />{{ p.createdAt }}</small>
        </span>
        <span class="up-c">
          <b *ngIf="loginDaysAgo() !== null">{{ 'userProfile.daysAgoShort' | translate: { days: loginDaysAgo() } }}</b>
          <b *ngIf="loginDaysAgo() === null">—</b>
          <small>{{ 'userProfile.lastLogin' | translate }}<br />{{ p.lastLoginAt ?? ('userProfile.untracked' | translate) }}</small>
        </span>
        <span class="up-c" *ngIf="p.age !== null">
          <b>{{ 'userProfile.years' | translate: { age: p.age } }}</b>
          <small *ngIf="p.ageBracket">{{ 'userProfile.bracket' | translate: { label: p.ageBracket.label } }}</small>
          <span class="up-pos" *ngIf="p.ageBracket">{{ 'userProfile.bracketShare' | translate: { share: p.ageBracket.share } }}</span>
        </span>
        <span class="up-c" *ngIf="p.nearby">
          <b>{{ p.nearby.total }}</b>
          <small>
            {{ 'userProfile.nearby' | translate: { radius: p.nearby.radiusInKm } }}
            <ng-container *ngFor="let sport of p.nearby.sameSport"><br />{{ 'userProfile.nearbySport' | translate: { count: sport.count, sport: sport.name } }}</ng-container>
          </small>
        </span>
        <span class="up-c" *ngIf="p.activityTopShare !== null">
          <b>{{ 'userProfile.topShare' | translate: { share: p.activityTopShare } }}</b>
          <small>{{ 'userProfile.topShareLabel' | translate }}</small>
        </span>
      </div>

      <!-- Les signaux vivants : chaque cas est code, la donnee decide. -->
      <div class="up-signal" *ngFor="let signal of p.signals">
        <strong>{{ 'userProfile.signalLabel' | translate }}</strong>
        {{ 'userProfile.signals.' + signal.key | translate: signal.params }}
      </div>
    </section>

    <div class="up-cols">
      <div class="up-col">
        <section class="up-card">
          <h2>{{ 'userProfile.timelineTitle' | translate }}</h2>
          <ul class="up-tl">
            <li *ngFor="let entry of p.timeline">
              <span class="up-k" [attr.data-kind]="entry.upcoming ? 'next' : entry.kind">
                {{ (entry.upcoming ? 'userProfile.kind.next' : 'userProfile.kind.' + entry.kind) | translate }}
              </span>
              <b>{{ entry.title }}</b>
              <span class="up-att" *ngIf="entry.kind === 'created'">
                · {{ 'userProfile.attendees' | translate: { count: entry.attendees > 0 ? entry.attendees - 1 : 0 } }}
              </span>
              <small>{{ entry.startDate }}</small>
            </li>
          </ul>
          <p class="up-muted" *ngIf="p.timeline.length === 0">{{ 'userProfile.noTimeline' | translate }}</p>
        </section>

        <section class="up-card">
          <h2>{{ 'userProfile.groupsTitle' | translate }}</h2>
          <div class="up-grpline" *ngFor="let group of p.groups">
            <span class="up-role" [class.is-admin]="group.isAdmin">
              {{ (group.isAdmin ? 'userProfile.admin' : 'userProfile.memberRole') | translate }}
            </span>
            <b>{{ group.name }}</b>
            <small>· {{ 'userProfile.groupMembers' | translate: { count: group.members } }}</small>
          </div>
          <p class="up-muted" *ngIf="p.groups.length === 0">{{ 'userProfile.noGroups' | translate }}</p>
        </section>

        <section class="up-card">
          <h2>{{ 'userProfile.moneyTitle' | translate }}</h2>
          <div class="up-money">
            <span class="up-mo"><b>{{ euros(p.money.paid) }}</b><small>{{ 'userProfile.paid' | translate }}</small></span>
            <span class="up-mo"><b>{{ euros(p.money.refunded) }}</b><small>{{ 'userProfile.refunded' | translate }}</small></span>
            <span class="up-mo"><b>{{ euros(p.money.released) }}</b><small>{{ 'userProfile.released' | translate }}</small></span>
          </div>
        </section>

        <section class="up-card" *ngIf="mapOptions() as options">
          <h2>{{ 'userProfile.positionTitle' | translate }}</h2>
          <google-map width="100%" height="220px" [options]="options">
            <map-marker *ngIf="markerPosition() as marker" [position]="marker" />
          </google-map>
        </section>
      </div>

      <div class="up-col">
        <section class="up-card">
          <h2>{{ 'userProfile.sportsTitle' | translate }}</h2>
          <span class="up-sport" *ngFor="let sport of p.sports">
            <b>{{ sport.name }}</b>
            <ng-container *ngIf="sport.level !== null"> {{ sport.level }}<ng-container *ngIf="sport.maxLevel">/{{ sport.maxLevel }}</ng-container></ng-container>
            <small *ngIf="sport.declaredAt">{{ 'userProfile.declaredAt' | translate: { date: sport.declaredAt } }}</small>
          </span>
          <p class="up-muted" *ngIf="p.sports.length === 0">{{ 'userProfile.noSports' | translate }}</p>
        </section>

        <section class="up-card">
          <h2>{{ 'userProfile.completenessTitle' | translate }}</h2>
          <div class="up-check">
            <span class="up-ck" *ngFor="let entry of p.completeness" [class.is-ok]="entry.filled" [class.is-miss]="!entry.filled">
              {{ 'userProfile.fields.' + entry.field | translate }}
            </span>
          </div>
        </section>

        <section class="up-card">
          <h2>{{ 'userProfile.reachTitle' | translate }}</h2>
          <p class="up-tokline">
            {{ 'userProfile.pushToken' | translate }}
            <b [class.is-ok]="p.hasPush" [class.is-miss]="!p.hasPush">
              {{ (p.hasPush ? 'userProfile.present' : 'userProfile.absent') | translate }}
            </b>
          </p>
          <ng-container *ngIf="p.hasCustomNotifications; else defaults">
            <div class="up-swgrp">
              <h3>{{ 'userProfile.pushGroup' | translate }}</h3>
              <span class="up-sw" *ngFor="let entry of pushEntries()" [class.is-on]="entry[1] !== false" [class.is-off]="entry[1] === false">
                {{ 'userProfile.switches.' + entry[0] | translate }}
              </span>
            </div>
            <div class="up-swgrp">
              <h3>{{ 'userProfile.mailGroup' | translate }}</h3>
              <span class="up-sw" *ngFor="let entry of mailEntries()" [class.is-on]="entry[1] !== false" [class.is-off]="entry[1] === false">
                {{ 'userProfile.switches.' + entry[0] | translate }}
              </span>
            </div>
          </ng-container>
          <ng-template #defaults>
            <p class="up-muted">{{ 'userProfile.defaultSettings' | translate }}</p>
          </ng-template>
        </section>
      </div>
    </div>
  </ng-container>
</div>
